import React, { useState, useEffect } from 'react';

interface QueueMonitoringMinimalProps {
  apiKey: string;
}

// Minimal version to test for infinite loops
export const QueueMonitoringMinimal: React.FC<QueueMonitoringMinimalProps> = ({ apiKey }) => {
  const [step, setStep] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  
  const addLog = (message: string) => {
    console.log(`[QueueMinimal] ${message}`);
    setLogs(prev => [...prev.slice(-10), `${new Date().toISOString()}: ${message}`]);
  };

  // Test 1: Basic component mounting
  useEffect(() => {
    addLog('Component mounted');
    setStep(1);
  }, []);

  // Test 2: Simple state update
  useEffect(() => {
    if (step === 1) {
      addLog('Step 1: Basic state update test');
      setTimeout(() => {
        setStep(2);
      }, 1000);
    }
  }, [step]);

  // Test 3: API call without complex logic
  useEffect(() => {
    if (step === 2) {
      addLog('Step 2: Testing simple API call');
      
      const testAPI = async () => {
        try {
          const response = await fetch('/api/v1/queue/health', {
            headers: { 'Authorization': `Bearer ${apiKey}` }
          });
          addLog(`API response: ${response.status}`);
          setStep(3);
        } catch (error) {
          addLog(`API error: ${error}`);
          setStep(3);
        }
      };
      
      testAPI();
    }
  }, [step, apiKey]);

  // Test 4: Multiple state updates
  useEffect(() => {
    if (step === 3) {
      addLog('Step 3: Multiple state updates test');
      let counter = 0;
      
      const interval = setInterval(() => {
        counter++;
        addLog(`Counter: ${counter}`);
        
        if (counter >= 3) {
          clearInterval(interval);
          setStep(4);
        }
      }, 500);
      
      return () => clearInterval(interval);
    }
  }, [step]);

  // Test 5: Final test
  useEffect(() => {
    if (step === 4) {
      addLog('Step 4: All tests complete - no infinite loop detected');
    }
  }, [step]);

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h2 className="text-xl font-bold mb-4">Queue Monitoring - Minimal Test</h2>
      
      <div className="mb-4">
        <div className="text-sm font-medium">Current Step: {step}</div>
        <div className="text-sm text-gray-600">
          {step === 0 && "Initializing..."}
          {step === 1 && "Testing basic state updates..."}
          {step === 2 && "Testing API connectivity..."}
          {step === 3 && "Testing multiple state updates..."}
          {step === 4 && "✅ All tests passed!"}
        </div>
      </div>

      <div className="bg-gray-100 p-3 rounded text-xs font-mono max-h-60 overflow-y-auto">
        <div className="font-bold mb-2">Debug Logs:</div>
        {logs.map((log, i) => (
          <div key={i} className="mb-1">{log}</div>
        ))}
      </div>

      <div className="mt-4 text-xs text-gray-500">
        Check browser console for detailed logs. If this component hangs, the issue is in React state management.
      </div>
    </div>
  );
};